import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Container,
  CircularProgress,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Support as SupportIcon,
  BugReport as BugReportIcon,
  Person as PersonIcon,
  Feedback as FeedbackIcon,
  Help as HelpIcon,
  Send as SendIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../../consts';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import TicketDetail from './TicketDetail';
import ReportTicketDialog from './ReportTicketDialog';

const getTypeIcon = (type) => {
  switch (type) {
    case 'bug':
      return <BugReportIcon />;
    case 'user_report':
      return <PersonIcon />;
    case 'feedback':
      return <FeedbackIcon />;
    default:
      return <HelpIcon />;
  }
};

const getTypeLabel = (type) => {
  switch (type) {
    case 'bug':
      return 'Something Not Working';
    case 'user_report':
      return 'Report a User';
    case 'feedback':
      return 'Share Feedback';
    default:
      return 'General Question';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'open':
      return 'Waiting for Response';
    case 'in_progress':
      return 'We\'re Working on It';
    case 'resolved':
      return 'Resolved';
    case 'closed':
      return 'Closed';
    default:
      return status;
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'open':
      return <PendingIcon />;
    case 'in_progress':
      return <HourglassEmptyIcon />;
    case 'resolved':
      return <CheckCircleIcon />;
    case 'closed':
      return <CancelIcon />;
    default:
      return <InfoIcon />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'open':
      return 'info';
    case 'in_progress':
      return 'warning';
    case 'resolved':
      return 'success';
    case 'closed':
      return 'default';
    default:
      return 'default';
  }
};

const MyTickets = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.token);
  const user = useSelector((state) => state.user);
  const setTokenInProcess = useSelector((state) => state.setTokenInProcess);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    // Wait for token restoration to complete before checking
    if (setTokenInProcess) {
      return;
    }

    // SECURITY: Check authentication - tokens are now in httpOnly cookies
    // Axios interceptor will automatically add token from cookies
    if (!token && !setTokenInProcess) {
      // Check if authenticated via Amplify session
      import('../../utils/authUtils').then(({ isAuthenticated }) => {
        isAuthenticated().then((authenticated) => {
          if (!authenticated) {
            navigate('/login');
            return;
          }
          // If authenticated, axios interceptor will handle token
          fetchTickets();
        }).catch(() => {
          navigate('/login');
        });
      });
    } else if (token || setTokenInProcess) {
      // Token available or in process, proceed
      fetchTickets();
    }
  }, [token, statusFilter, typeFilter, setTokenInProcess, navigate]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;

      // SECURITY: Token is now in httpOnly cookies, axios interceptor handles it automatically
      const response = await axios.get(`${API_URL}/tickets`, {
        params,
      });

      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      if (error.response?.status === 401) {
        dispatch({ type: 'SET_UNAUTHORIZED' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = () => {
    setReportDialogOpen(true);
  };

  const handleTicketCreated = () => {
    setReportDialogOpen(false);
    fetchTickets();
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setDetailDialogOpen(true);
  };

  const handleTicketUpdated = () => {
    setDetailDialogOpen(false);
    setSelectedTicket(null);
    fetchTickets();
  };

  if (loading && tickets.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <Box>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Help & Support
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600 }}>
              Need help? We're here for you! Contact our support team or check the status of your previous requests.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTicket}
            size="large"
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
              },
              boxShadow: 3,
            }}
          >
            Get Help
          </Button>
        </Box>

        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={statusFilter}
              label="Filter by Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="open">Waiting for Response</MenuItem>
              <MenuItem value="in_progress">We're Working on It</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by Type</InputLabel>
            <Select
              value={typeFilter}
              label="Filter by Type"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="bug">Something Not Working</MenuItem>
              <MenuItem value="user_report">Report a User</MenuItem>
              <MenuItem value="feedback">Share Feedback</MenuItem>
              <MenuItem value="other">General Question</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {tickets.length === 0 ? (
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <SupportIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              {statusFilter || typeFilter
                ? 'No requests match your filters'
                : "You're all set!"}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
              {statusFilter || typeFilter
                ? 'Try adjusting your filters to see more requests, or create a new support request if you need help.'
                : "You haven't contacted support yet. If you need help with anything, we're here for you!"}
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleCreateTicket}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
                },
                px: 4,
                py: 1.5,
              }}
            >
              Get Help Now
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {tickets.map((ticket) => (
            <Grid item xs={12} key={ticket.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: theme.shadows[12],
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                  },
                }}
                onClick={() => handleTicketClick(ticket)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="flex-start" gap={2.5} flex={1}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          width: 56,
                          height: 56,
                        }}
                      >
                        {getTypeIcon(ticket.type)}
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          {ticket.subject}
                        </Typography>
                        <Box display="flex" gap={1.5} mt={1} flexWrap="wrap" alignItems="center">
                          <Chip
                            icon={getStatusIcon(ticket.status)}
                            label={getStatusLabel(ticket.status)}
                            size="medium"
                            color={getStatusColor(ticket.status)}
                            sx={{
                              fontWeight: 500,
                              '& .MuiChip-icon': {
                                color: 'inherit',
                              },
                            }}
                          />
                          <Chip
                            label={getTypeLabel(ticket.type)}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </Box>
                        {ticket.last_message && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                              borderRadius: 2,
                              borderLeft: `3px solid ${theme.palette.primary.main}`,
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.6,
                              }}
                            >
                              {ticket.last_message.message}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right', ml: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Last updated
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {new Date(ticket.update_time).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {new Date(ticket.update_time).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <ReportTicketDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        onSuccess={handleTicketCreated}
      />

      {selectedTicket && (
        <TicketDetail
          open={detailDialogOpen}
          ticketId={selectedTicket.id}
          onClose={() => {
            setDetailDialogOpen(false);
            setSelectedTicket(null);
          }}
          onUpdate={handleTicketUpdated}
          isUserView={true}
        />
      )}
    </Container>
  );
};

export default MyTickets;

